import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUpDown, ImagePlus, Loader2, Plus, Search, Sparkles, Trash2, X } from "lucide-react";
import { api, apiError, fileUrl, inr, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, ListState } from "@/components/Layout";
import { BulkProductIngest } from "@/components/BulkProductIngest";
import {
  CategoryReorder, ProductCardActions, ProductDeleteDialog, ProductEditDialog, ProductTrash,
} from "@/components/ProductAdmin";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SEARCH_DEBOUNCE_MS = 250;
const dragRef = { current: null };

const EMPTY = {
  name: "", sku: "", category: "Water Slides", description: "", photos: [],
  dimensions: "", height: "", length: "", width: "", weight: "", capacity: "",
  material: "FRP", colour: "", manufacturing_cost: 0, standard_price: 0, minimum_price: 0,
  gst_rate: 18, hsn_sac: "9506", unit: "Nos", installation_charge: 0, transportation_charge: 0,
  warranty: "12 months", specifications: [], item_type: "finished_good",
};

export default function Products() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catMeta, setCatMeta] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [subcategory, setSubcategory] = useState("all");
  const [view, setView] = useState("active");
  const [trash, setTrash] = useState({ items: [], can_purge: false });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [reorder, setReorder] = useState(false);
  const [positions, setPositions] = useState({});
  const [catOrderOpen, setCatOrderOpen] = useState(false);
  const fileRef = useRef(null);

  const subMap = Object.fromEntries(catMeta.map((c) => [c.name, c.subcategories || []]));

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/product-categories");
      setCatMeta(data.categories);
      setCategories(data.categories.map((c) => c.name));
    } catch {
      /* falls back to the categories returned with the product list */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (view === "trash") {
        const { data } = await api.get(`/product-trash${search ? `?search=${search}` : ""}`);
        setTrash(data);
      } else {
        const params = new URLSearchParams({ limit: "200" });
        if (search) params.set("search", search);
        if (category !== "all") params.set("category", category);
        if (subcategory !== "all") params.set("subcategory", subcategory);
        const { data } = await api.get(`/products?${params}`);
        setItems(data.items);
        setCategories((c) => (c.length ? c : data.categories));
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, category, subcategory, view]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  useEffect(() => {
    const t = setTimeout(load, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [load]);

  const savePositions = async () => {
    const orders = Object.entries(positions)
      .map(([id, display_order]) => ({ id, display_order: Number(display_order) || 0 }));
    if (!orders.length) { setReorder(false); return; }
    try {
      await api.put("/product-order", { orders });
      toast.success("Order saved — quotations will follow this sequence");
      setPositions({});
      setReorder(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const dropOn = async (targetId) => {
    const fromId = dragRef.current;
    if (!fromId || fromId === targetId) return;
    const list = [...items];
    const from = list.findIndex((p) => p.id === fromId);
    const to = list.findIndex((p) => p.id === targetId);
    list.splice(to, 0, list.splice(from, 1)[0]);
    setItems(list);
    try {
      await api.put("/product-order",
        { orders: list.map((p, i) => ({ id: p.id, display_order: i + 1 })) });
      toast.success("Order saved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addPhotos = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const res = await uploadFile(f, "product");
        urls.push(res.url);
      }
      setForm((f) => ({ ...f, photos: [...f.photos, ...urls] }));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    try {
      if (!form.name.trim()) return toast.error("Product name is required");
      const numeric = ["manufacturing_cost", "standard_price", "minimum_price", "gst_rate",
        "installation_charge", "transportation_charge"];
      const payload = { ...form };
      numeric.forEach((k) => (payload[k] = Number(payload[k] || 0)));
      await api.post("/products", payload);
      toast.success("Product added to catalogue");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      <PageHeader
        testId="products-header"
        title="Product Master"
        subtitle="Photographs, specifications, cost and price for every product you manufacture."
        actions={
          can("products", "create") && (
            <>
            <Button variant="outline" className="gap-1.5" data-testid="bulk-ingest-button"
              onClick={() => setBulkOpen(true)}>
              <Sparkles className="h-4 w-4" /> Add from photos
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-product-button" className="gap-1.5">
                  <Plus className="h-4 w-4" /> New Product
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>New product</DialogTitle>
                </DialogHeader>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Product name" className="sm:col-span-2">
                    <Input
                      data-testid="product-name-input"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </Field>
                  <Field label="Category">
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="product-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Item type">
                    <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v })}>
                      <SelectTrigger data-testid="product-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["finished_good", "raw_material", "consumable", "service"].map((t) => (
                          <SelectItem key={t} value={t}>
                            {t.replace("_", " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  {[
                    ["sku", "SKU / Code", null],
                    ["hsn_sac", "HSN / SAC", null],
                    ["standard_price", "Standard price (₹)", "product-price-input"],
                    ["minimum_price", "Minimum price (₹)", null],
                    ["manufacturing_cost", "Manufacturing cost (₹)", "product-cost-input"],
                    ["gst_rate", "GST %", null],
                    ["unit", "Unit", null],
                    ["dimensions", "Dimensions", null],
                    ["capacity", "Capacity", null],
                    ["material", "Material", null],
                    ["colour", "Colour", null],
                    ["installation_charge", "Installation charge (₹)", null],
                    ["transportation_charge", "Transportation charge (₹)", null],
                    ["warranty", "Warranty", null],
                  ].map(([key, label, tid]) => (
                    <Field key={key} label={label}>
                      <Input
                        data-testid={tid || undefined}
                        value={form[key]}
                        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      />
                    </Field>
                  ))}
                  <Field label="Description" className="sm:col-span-2">
                    <Textarea
                      data-testid="product-description-input"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                  </Field>
                  <div className="sm:col-span-2">
                    <div className="overline mb-2">Photographs</div>
                    <div className="flex flex-wrap gap-2">
                      {form.photos.map((p) => (
                        <div key={p} className="product-image-frame relative h-20 w-24 border border-border">
                          <img src={fileUrl(p)} alt="" />
                          <button
                            type="button"
                            onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((x) => x !== p) }))}
                            className="absolute right-1 top-1 rounded bg-background/90 p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        data-testid="product-photo-upload"
                        onClick={() => fileRef.current?.click()}
                        className="flex h-20 w-24 flex-col items-center justify-center gap-1 rounded border border-dashed border-border text-xs text-muted-foreground hover:bg-secondary"
                      >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                        Add
                      </button>
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        onChange={(e) => addPhotos(Array.from(e.target.files || []))}
                      />
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <div className="overline mb-2">Specifications</div>
                    {form.specifications.map((s, i) => (
                      <div key={s.uid || `spec-${i}`} className="mb-2 flex gap-2">
                        <Input
                          placeholder="Label"
                          value={s.label}
                          onChange={(e) => {
                            const next = [...form.specifications];
                            next[i] = { ...next[i], label: e.target.value };
                            setForm({ ...form, specifications: next });
                          }}
                        />
                        <Input
                          placeholder="Value"
                          value={s.value}
                          onChange={(e) => {
                            const next = [...form.specifications];
                            next[i] = { ...next[i], value: e.target.value };
                            setForm({ ...form, specifications: next });
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setForm({ ...form, specifications: form.specifications.filter((_, x) => x !== i) })
                          }
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="product-add-spec-button"
                      onClick={() =>
                        setForm({ ...form, specifications: [...form.specifications, { uid: `spec-${Date.now()}`, label: "", value: "" }] })
                      }
                    >
                      Add specification
                    </Button>
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="product-save-button" onClick={save}>
                    Save product
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3 sm:px-6">
        <div className="flex rounded-md border border-border p-0.5" data-testid="products-view-toggle">
          <button type="button" data-testid="view-active-button"
            className={`rounded px-3 py-1 text-xs ${view === "active" ? "bg-primary text-primary-foreground" : ""}`}
            onClick={() => setView("active")}>Catalogue</button>
          {can("products", "delete") && (
            <button type="button" data-testid="view-trash-button"
              className={`flex items-center gap-1 rounded px-3 py-1 text-xs ${view === "trash" ? "bg-primary text-primary-foreground" : ""}`}
              onClick={() => setView("trash")}>
              <Trash2 className="h-3 w-3" /> Trash
            </button>
          )}
        </div>

        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="products-search-input"
            className="pl-9"
            placeholder="Search product title or SKU"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {view === "active" && (
          <>
            <Select value={category}
              onValueChange={(v) => { setCategory(v); setSubcategory("all"); }}>
              <SelectTrigger data-testid="products-category-filter" className="w-52">
                <SelectValue placeholder="Main category" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All main categories</SelectItem>
                {(catMeta.length ? catMeta.map((c) => c.name) : categories).map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                    {catMeta.find((m) => m.name === c)?.products
                      ? ` (${catMeta.find((m) => m.name === c).products})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={subcategory} onValueChange={setSubcategory}
              disabled={category === "all" || !(subMap[category] || []).length}>
              <SelectTrigger data-testid="products-subcategory-filter" className="w-52">
                <SelectValue placeholder="Sub-category" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All sub-categories</SelectItem>
                {(subMap[category] || []).map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {can("products", "edit") && (
              <>
                <Button variant="outline" size="sm" className="gap-1.5"
                  data-testid="category-reorder-button" onClick={() => setCatOrderOpen(true)}>
                  <ArrowUpDown className="h-3.5 w-3.5" /> Arrange categories
                </Button>
                {reorder ? (
                  <>
                    <Button size="sm" data-testid="products-save-order-button"
                      onClick={savePositions}>Save order</Button>
                    <Button size="sm" variant="ghost" data-testid="products-cancel-order-button"
                      onClick={() => { setReorder(false); setPositions({}); }}>Cancel</Button>
                  </>
                ) : (
                  <Button variant="outline" size="sm" className="gap-1.5"
                    data-testid="products-reorder-button" onClick={() => setReorder(true)}>
                    <ArrowUpDown className="h-3.5 w-3.5" /> Reorder mode
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </div>

      <div className="p-4 sm:p-6">
        {view === "trash" ? (
          <ListState loading={loading} isEmpty={false}>
            <ProductTrash rows={trash.items} canPurge={trash.can_purge} onChanged={load} />
          </ListState>
        ) : (
        <ListState loading={loading} isEmpty={items.length === 0}
          emptyTitle="No products found"
          emptyHint="Add your manufacturing catalogue with photographs.">
            {reorder && (
              <p className="mb-3 text-xs text-muted-foreground" data-testid="reorder-hint">
                Drag a card to move it, or type its position number. Lower numbers show first —
                in this list and in the quotation item picker.
              </p>
            )}
            <div
              className="stagger grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              data-testid="products-grid"
            >
              {items.map((p, idx) => (
                <div
                  key={p.id}
                  data-testid={`product-card-${p.id}`}
                  draggable={reorder}
                  onDragStart={() => { dragRef.current = p.id; }}
                  onDragOver={(e) => reorder && e.preventDefault()}
                  onDrop={() => reorder && dropOn(p.id)}
                  className={`surface overflow-hidden transition-transform hover:-translate-y-1 ${reorder ? "cursor-grab" : ""}`}
                >
                  <div className="product-image-container h-40">
                    {p.photos?.[0] ? (
                      <img src={fileUrl(p.photos[0])} alt={p.name} />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                        No photograph
                      </div>
                    )}
                    {p.photos?.length > 1 && (
                      <Badge className="absolute bottom-2 right-2 bg-background/90 text-foreground">
                        +{p.photos.length - 1} photos
                      </Badge>
                    )}
                    {p.is_demo && (
                      <Badge className="absolute left-2 top-2 bg-accent text-accent-foreground text-[9px]">
                        DEMO DATA
                      </Badge>
                    )}
                    {reorder ? (
                      <Input
                        className="absolute right-2 top-2 h-7 w-16 bg-background text-center text-xs"
                        data-testid={`product-position-${p.id}`}
                        value={positions[p.id] ?? (p.display_order || idx + 1)}
                        onChange={(e) => setPositions({ ...positions, [p.id]: e.target.value })}
                      />
                    ) : (
                      <ProductCardActions
                        product={p}
                        canEdit={can("products", "edit")}
                        canDelete={can("products", "delete")}
                        onEdit={setEditing}
                        onDelete={setDeleting}
                      />
                    )}
                  </div>
                  <div className="p-4">
                    <div className="overline">
                      {p.category}{p.subcategory ? ` • ${p.subcategory}` : ""}
                    </div>
                    <div className="mt-1 line-clamp-2 text-sm font-semibold">{p.name}</div>
                    <div className="mt-3 flex items-end justify-between">
                      <div>
                        <div className="font-mono-num text-lg font-semibold">{inr(p.standard_price)}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {p.sku ? `${p.sku} • ` : ""}HSN {p.hsn_sac || "-"} • GST {p.gst_rate}%
                        </div>
                      </div>
                      <Badge variant="outline" className="text-[10px]">
                        {p.unit}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
        </ListState>
        )}
      </div>
      <BulkProductIngest open={bulkOpen} onOpenChange={setBulkOpen} categories={categories}
        onSaved={load} />
      {editing && (
        <ProductEditDialog product={editing} categories={categories} subcategories={subMap}
          onClose={() => setEditing(null)} onSaved={() => { load(); loadCategories(); }} />
      )}
      {deleting && (
        <ProductDeleteDialog product={deleting} onClose={() => setDeleting(null)}
          onDone={load} />
      )}
      {catOrderOpen && (
        <CategoryReorder open={catOrderOpen} onOpenChange={setCatOrderOpen}
          categories={catMeta} onSaved={loadCategories} />
      )}
    </div>
  );
}
